import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { toast } from 'sonner';
import { 
  Settings, 
  Upload, 
  Save, 
  Globe, 
  Phone, 
  Mail, 
  MapPin, 
  Palette, 
  Share2,
  Eye,
  History,
  RefreshCw
} from 'lucide-react';

const SiteSettings = () => {
  const { API } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [showHistory, setShowHistory] = useState(false);
  const [settings, setSettings] = useState({
    site_title: '',
    meta_description: '',
    meta_keywords: [],
    site_logo_url: '',
    use_custom_logo: false,
    site_name: '',
    site_slogan: '',
    company_description: '',
    hero_title: '',
    hero_subtitle: '',
    hero_background_color: 'gradient',
    hero_background_image_url: '',
    hero_text_color: '#ffffff',
    contact_phone: '',
    contact_email: '',
    contact_address: '',
    whatsapp_business: '',
    facebook_url: '',
    instagram_url: '',
    twitter_url: '',
    linkedin_url: '',
    youtube_url: '',
    site_color_primary: '#22c55e',
    site_color_secondary: '#fb923c',
    maintenance_mode: false,
    registration_enabled: true
  });
  const [history, setHistory] = useState([]);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [keywordsInput, setKeywordsInput] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API}/admin/settings`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSettings(data);
        setKeywordsInput(data.meta_keywords.join(', '));
      } else {
        toast.error('Erro ao carregar configurações');
      }
    } catch (error) {
      toast.error('Erro ao conectar com o servidor');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await fetch(`${API}/admin/settings/history`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setHistory(data);
      } else {
        toast.error('Erro ao carregar histórico');
      }
    } catch (error) {
      toast.error('Erro ao conectar com o servidor');
    }
  };

  const handleInputChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleLogoChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        setLogoFile(file);
        const reader = new FileReader();
        reader.onload = (e) => {
          setLogoPreview(e.target.result);
        };
        reader.readAsDataURL(file);
      } else {
        toast.error('Por favor, selecione apenas arquivos de imagem');
      }
    }
  };

  const uploadLogo = async () => {
    if (!logoFile) return null;

    const formData = new FormData();
    formData.append('logo', logoFile);

    try {
      const response = await fetch(`${API}/admin/settings/logo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Logo atualizado com sucesso!');
        return data.logo_url;
      } else {
        toast.error('Erro ao fazer upload da logo');
        return null;
      }
    } catch (error) {
      toast.error('Erro ao fazer upload da logo');
      return null;
    }
  };

  const handleSave = async () => {
    setSaving(true);
    
    try {
      // Upload logo if there's a new one
      let logoUrl = settings.site_logo_url;
      if (logoFile) {
        logoUrl = await uploadLogo();
        if (!logoUrl) {
          setSaving(false);
          return;
        }
      }

      // Parse keywords
      const keywords = keywordsInput
        .split(',')
        .map(k => k.trim())
        .filter(k => k.length > 0);

      const updatedSettings = {
        ...settings,
        site_logo_url: logoUrl,
        meta_keywords: keywords
      };

      const response = await fetch(`${API}/admin/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(updatedSettings)
      });

      if (response.ok) {
        toast.success('Configurações salvas com sucesso!');
        setLogoFile(null);
        setLogoPreview(null);
        await fetchSettings(); // Reload to get updated data
      } else {
        toast.error('Erro ao salvar configurações');
      }
    } catch (error) {
      toast.error('Erro ao conectar com o servidor');
    } finally {
      setSaving(false);
    }
  };

  const handleHistoryToggle = () => {
    setShowHistory(!showHistory);
    if (!showHistory) {
      fetchHistory();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
      </div>
    );
  }

  const tabs = [
    { id: 'general', name: 'Geral', icon: Settings },
    { id: 'hero', name: 'Banner Inicial', icon: Eye },
    { id: 'seo', name: 'SEO', icon: Globe },
    { id: 'contact', name: 'Contato', icon: Phone },
    { id: 'social', name: 'Redes Sociais', icon: Share2 },
    { id: 'appearance', name: 'Aparência', icon: Palette }
  ];

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div className="border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Settings className="w-6 h-6 text-green-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              Configurações do Site
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleHistoryToggle}
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              <History className="w-4 h-4" />
              Histórico
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
            >
              {saving ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Salvar Alterações
            </button>
          </div>
        </div>
      </div>

      {showHistory ? (
        /* History View */
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              Histórico de Alterações
            </h3>
            <button
              onClick={() => setShowHistory(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              ×
            </button>
          </div>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {history.length === 0 ? (
              <p className="text-gray-500 text-center py-4">
                Nenhuma alteração registrada
              </p>
            ) : (
              history.map((item, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">
                      {item.field_changed.replace(/_/g, ' ').toUpperCase()}
                    </span>
                    <span className="text-sm text-gray-500">
                      {new Date(item.created_at).toLocaleString('pt-BR')}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Valor anterior:</span>
                      <p className="text-red-600 break-words">{item.old_value || 'Vazio'}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">Novo valor:</span>
                      <p className="text-green-600 break-words">{item.new_value || 'Vazio'}</p>
                    </div>
                  </div>
                  <div className="mt-2 text-sm text-gray-600">
                    Alterado por: <span className="font-medium">{item.changed_by_name}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      ) : (
        /* Main Settings View */
        <>
          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="px-6 flex space-x-8">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === tab.id
                        ? 'border-green-500 text-green-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.name}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6 space-y-6">
            {/* General Tab */}
            {activeTab === 'general' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Título do Site (SEO)
                    </label>
                    <input
                      type="text"
                      value={settings.site_title}
                      onChange={(e) => handleInputChange('site_title', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="Nossa Feira - Marketplace Local"
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      Aparece na aba do navegador
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nome ao Lado do Logo
                    </label>
                    <input
                      type="text"
                      value={settings.site_name}
                      onChange={(e) => handleInputChange('site_name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="Nossa Feira"
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      Aparece ao lado do logo no navbar
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Slogan
                  </label>
                  <input
                    type="text"
                    value={settings.site_slogan}
                    onChange={(e) => handleInputChange('site_slogan', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Conectando produtores locais a consumidores diretos"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Descrição da Empresa
                  </label>
                  <textarea
                    value={settings.company_description}
                    onChange={(e) => handleInputChange('company_description', e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Descrição sobre a empresa..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Logo do Site
                  </label>
                  
                  {/* Opção para escolher entre ícone padrão ou logo personalizada */}
                  <div className="mb-4">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={settings.use_custom_logo}
                        onChange={(e) => handleInputChange('use_custom_logo', e.target.checked)}
                        className="w-4 h-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-700">Usar logo personalizada</span>
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                      {settings.use_custom_logo 
                        ? 'Uma logo personalizada será exibida' 
                        : 'O ícone padrão (cesta verde) será mantido'}
                    </p>
                  </div>

                  {settings.use_custom_logo && (
                    <div className="flex items-center gap-4">
                      <div className="flex-1">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleLogoChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                        />
                      </div>
                      {(logoPreview || settings.site_logo_url) && (
                        <div className="w-16 h-16 border border-gray-300 rounded-lg overflow-hidden">
                          <img
                            src={logoPreview || `${settings.site_logo_url}`}
                            alt="Logo preview"
                            className="w-full h-full object-contain"
                          />
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Preview do resultado */}
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Preview do Logo</h4>
                    <div className="flex items-center gap-2">
                      {settings.use_custom_logo && (logoPreview || settings.site_logo_url) ? (
                        <img 
                          src={logoPreview || `${settings.site_logo_url}`}
                          alt="Logo preview"
                          className="w-10 h-10 object-contain"
                        />
                      ) : (
                        <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-orange-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-xs">🛒</span>
                        </div>
                      )}
                      <span className="text-xl font-bold text-green-600">
                        {settings.site_name || 'Nossa Feira'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
                    Configurações de Acesso
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="maintenance_mode"
                        checked={settings.maintenance_mode}
                        onChange={(e) => handleInputChange('maintenance_mode', e.target.checked)}
                        className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                      />
                      <label htmlFor="maintenance_mode" className="ml-2 block text-sm text-gray-900">
                        Modo de Manutenção
                      </label>
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="registration_enabled"
                        checked={settings.registration_enabled}
                        onChange={(e) => handleInputChange('registration_enabled', e.target.checked)}
                        className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                      />
                      <label htmlFor="registration_enabled" className="ml-2 block text-sm text-gray-900">
                        Permitir Novos Cadastros
                      </label>
                    </div>
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="whatsapp_requires_login"
                          checked={settings.whatsapp_requires_login}
                          onChange={(e) => handleInputChange('whatsapp_requires_login', e.target.checked)}
                          className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                        />
                      </div>
                      <div className="flex-1">
                        <label htmlFor="whatsapp_requires_login" className="block text-sm font-medium text-gray-900">
                          Exigir Login para Contatar Produtores
                        </label>
                        <p className="text-xs text-gray-600 mt-1">
                          {settings.whatsapp_requires_login 
                            ? '🔒 Usuários precisam estar logados para acessar o WhatsApp dos produtores' 
                            : '🔓 Qualquer visitante pode contatar os produtores via WhatsApp (sem necessidade de login)'
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Hero Section Tab */}
            {activeTab === 'hero' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Título Principal
                    </label>
                    <input
                      type="text"
                      value={settings.hero_title}
                      onChange={(e) => handleInputChange('hero_title', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="Nossa Feira"
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      Título grande que aparece na página inicial
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Subtítulo
                    </label>
                    <input
                      type="text"
                      value={settings.hero_subtitle}
                      onChange={(e) => handleInputChange('hero_subtitle', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="Conectando produtores locais a consumidores diretos"
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      Subtítulo que aparece abaixo do título principal
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo de Fundo
                  </label>
                  <select
                    value={settings.hero_background_color}
                    onChange={(e) => handleInputChange('hero_background_color', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value="gradient">Gradiente (Verde-Laranja)</option>
                    <option value="solid">Cor Sólida</option>
                    <option value="image">Imagem de Fundo</option>
                  </select>
                </div>

                {settings.hero_background_color === 'image' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      URL da Imagem de Fundo
                    </label>
                    <input
                      type="url"
                      value={settings.hero_background_image_url}
                      onChange={(e) => handleInputChange('hero_background_image_url', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="https://exemplo.com/imagem.jpg"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cor do Texto
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={settings.hero_text_color}
                      onChange={(e) => handleInputChange('hero_text_color', e.target.value)}
                      className="h-10 w-20 border border-gray-300 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={settings.hero_text_color}
                      onChange={(e) => handleInputChange('hero_text_color', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="#ffffff"
                    />
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Preview do Banner</h4>
                  <div 
                    className="relative rounded-lg overflow-hidden h-40 flex items-center justify-center text-center"
                    style={{
                      background: settings.hero_background_color === 'gradient' 
                        ? 'linear-gradient(135deg, #22c55e 0%, #fbbf24 50%, #fb923c 100%)'
                        : settings.hero_background_color === 'solid'
                        ? settings.site_color_primary
                        : settings.hero_background_image_url 
                        ? `url(${settings.hero_background_image_url}) center/cover`
                        : 'linear-gradient(135deg, #22c55e 0%, #fbbf24 50%, #fb923c 100%)',
                      color: settings.hero_text_color
                    }}
                  >
                    <div>
                      <h1 className="text-2xl font-bold mb-2">{settings.hero_title || 'Nossa Feira'}</h1>
                      <p className="text-sm opacity-90">{settings.hero_subtitle || 'Conectando produtores locais a consumidores diretos'}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* SEO Tab */}
            {activeTab === 'seo' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Meta Description
                  </label>
                  <textarea
                    value={settings.meta_description}
                    onChange={(e) => handleInputChange('meta_description', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Descrição que aparece nos resultados de busca..."
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Recomendado: 150-160 caracteres
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Palavras-chave (SEO)
                  </label>
                  <input
                    type="text"
                    value={keywordsInput}
                    onChange={(e) => setKeywordsInput(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="marketplace, produtos locais, produtores rurais..."
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Separe as palavras-chave com vírgulas
                  </p>
                </div>
              </div>
            )}

            {/* Contact Tab */}
            {activeTab === 'contact' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Telefone de Contato
                    </label>
                    <input
                      type="text"
                      value={settings.contact_phone}
                      onChange={(e) => handleInputChange('contact_phone', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="+55 (77) 9 9999-9999"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      E-mail de Contato
                    </label>
                    <input
                      type="email"
                      value={settings.contact_email}
                      onChange={(e) => handleInputChange('contact_email', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="contato@nossafeira.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Endereço
                  </label>
                  <input
                    type="text"
                    value={settings.contact_address}
                    onChange={(e) => handleInputChange('contact_address', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Bom Jesus da Lapa, BA"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    WhatsApp Business
                  </label>
                  <input
                    type="text"
                    value={settings.whatsapp_business}
                    onChange={(e) => handleInputChange('whatsapp_business', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="+5577999999999"
                  />
                </div>
              </div>
            )}

            {/* Social Media Tab */}
            {activeTab === 'social' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Facebook
                    </label>
                    <input
                      type="url"
                      value={settings.facebook_url}
                      onChange={(e) => handleInputChange('facebook_url', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="https://facebook.com/nossafeira"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Instagram
                    </label>
                    <input
                      type="url"
                      value={settings.instagram_url}
                      onChange={(e) => handleInputChange('instagram_url', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="https://instagram.com/nossafeira"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Twitter
                    </label>
                    <input
                      type="url"
                      value={settings.twitter_url}
                      onChange={(e) => handleInputChange('twitter_url', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="https://twitter.com/nossafeira"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      LinkedIn
                    </label>
                    <input
                      type="url"
                      value={settings.linkedin_url}
                      onChange={(e) => handleInputChange('linkedin_url', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="https://linkedin.com/company/nossafeira"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      YouTube
                    </label>
                    <input
                      type="url"
                      value={settings.youtube_url}
                      onChange={(e) => handleInputChange('youtube_url', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="https://youtube.com/@nossafeira"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Appearance Tab */}
            {activeTab === 'appearance' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cor Primária
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={settings.site_color_primary}
                        onChange={(e) => handleInputChange('site_color_primary', e.target.value)}
                        className="h-10 w-20 border border-gray-300 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={settings.site_color_primary}
                        onChange={(e) => handleInputChange('site_color_primary', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="#22c55e"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cor Secundária
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={settings.site_color_secondary}
                        onChange={(e) => handleInputChange('site_color_secondary', e.target.value)}
                        className="h-10 w-20 border border-gray-300 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={settings.site_color_secondary}
                        onChange={(e) => handleInputChange('site_color_secondary', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="#fb923c"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Preview das Cores</h4>
                  <div className="flex items-center gap-4">
                    <div 
                      className="w-16 h-16 rounded-lg border border-gray-300"
                      style={{ backgroundColor: settings.site_color_primary }}
                      title="Cor Primária"
                    />
                    <div 
                      className="w-16 h-16 rounded-lg border border-gray-300"
                      style={{ backgroundColor: settings.site_color_secondary }}
                      title="Cor Secundária"
                    />
                    <div className="flex-1">
                      <p className="text-sm text-gray-600">
                        As cores serão aplicadas nos botões, links e elementos principais do site.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default SiteSettings;